import { formatMoney, formatNumber, formatDate, SOURCE_LABELS } from './reportFormat';

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[ch]));

function reportNumber(now) {
  const pad = (n) => String(n).padStart(2, '0');
  return `RPT-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

/**
 * Opens the category sales report as a print-ready, invoice-styled A4 page.
 *
 * mode "invoice" shows sales figures only (safe to hand out); mode "financial"
 * adds estimated cost and profit columns for internal use.
 *
 * Returns false when the browser blocked the popup so the caller can say so.
 */
export default function printCategoryReport(report, { filterLabels, issuedBy, mode = 'invoice' }) {
  const financial = mode === 'financial';
  const now = new Date();
  const { summary, categories } = report;

  const productColumns = financial ? 7 : 5;

  const categorySections = categories
    .map((category, ci) => {
      const rows = category.products
        .map(
          (p, i) => `
          <tr>
            <td class="c muted">${i + 1}</td>
            <td>
              <div class="pname">${escapeHtml(p.name)}</div>
              ${p.barcode ? `<div class="barcode">${escapeHtml(p.barcode)}</div>` : ''}
            </td>
            <td class="c">${formatNumber(p.unitsSold)}</td>
            <td class="c">${formatMoney(p.avgUnitPrice)}</td>
            ${financial ? `<td class="c">${formatMoney(p.cost)}</td>` : ''}
            ${financial ? `<td class="c ${p.profit < 0 ? 'neg' : 'pos'}">${formatMoney(p.profit)}</td>` : ''}
            <td class="c strong">${formatMoney(p.revenue)}</td>
          </tr>`
        )
        .join('');

      return `
      <section class="category">
        <div class="cat-head">
          <div class="cat-title">
            <span class="cat-index">${ci + 1}</span>
            <span>${escapeHtml(category.name)}</span>
          </div>
          <div class="cat-meta">
            <span>${formatNumber(category.salesCount)} عملية</span>
            <span>${formatNumber(category.unitsSold)} وحدة</span>
            <span>${category.share}% من الإيرادات</span>
          </div>
        </div>
        <table class="items">
          <thead>
            <tr>
              <th class="c" style="width:36px">#</th>
              <th>المنتج</th>
              <th class="c">الكمية</th>
              <th class="c">متوسط السعر</th>
              ${financial ? '<th class="c">التكلفة*</th><th class="c">الربح*</th>' : ''}
              <th class="c">الإجمالي</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td colspan="${productColumns - 1}" class="subtotal-label">إجمالي ${escapeHtml(category.name)}</td>
              <td class="c subtotal">${formatMoney(category.revenue)}</td>
            </tr>
          </tfoot>
        </table>
      </section>`;
    })
    .join('');

  const filterRows = [
    ['الفترة', `${formatDate(report.startDate)} — ${formatDate(report.endDate)}`],
    ['التصنيفات', filterLabels.categories],
    ['البائع', filterLabels.cashier],
    ['المصدر', SOURCE_LABELS[filterLabels.source] || SOURCE_LABELS.all],
  ]
    .map(([k, v]) => `<tr><td class="k">${k}</td><td class="v">${escapeHtml(v)}</td></tr>`)
    .join('');

  const totals = [
    ['عدد العمليات', formatNumber(summary.salesCount)],
    ['الوحدات المباعة', formatNumber(summary.unitsSold)],
    ['متوسط قيمة البيع', formatMoney(summary.avgSaleValue)],
    ...(financial
      ? [
          ['التكلفة التقديرية*', formatMoney(summary.cost)],
          ['الربح التقديري*', `${formatMoney(summary.profit)} (${summary.margin}%)`],
        ]
      : []),
  ]
    .map(([k, v]) => `<div class="row"><span>${k}</span><span>${v}</span></div>`)
    .join('');

  const title = financial ? 'تقرير مالي حسب التصنيف' : 'فاتورة مبيعات حسب التصنيف';
  const number = reportNumber(now);

  const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <title>${title} ${number}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />
  <style>
    @page { size: A4; margin: 12mm; }
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Cairo',sans-serif; background:#F9F7F3; color:#1f2937; font-size:12px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .toolbar { display:flex; justify-content:center; gap:10px; padding:18px; }
    .toolbar button { font-family:inherit; font-size:14px; font-weight:700; border:none; border-radius:12px; padding:10px 28px; cursor:pointer; }
    .btn-print { background:#6B4A2E; color:#fff; box-shadow:0 4px 12px rgba(107,74,46,.3); }
    .btn-close { background:#fff; color:#6B4A2E; border:1px solid #DBBF96 !important; }
    .sheet { max-width:820px; margin:0 auto 40px; background:#fff; border-radius:16px; box-shadow:0 4px 24px rgba(0,0,0,.08); overflow:hidden; }
    .header { background:linear-gradient(135deg,#201108,#6B4A2E); color:#fff; padding:28px 36px; display:flex; justify-content:space-between; align-items:center; gap:16px; }
    .brand { display:flex; align-items:center; gap:12px; }
    .logo { width:46px; height:46px; border-radius:12px; background:#F5CE26; color:#3B210E; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:20px; }
    .brand h1 { font-size:22px; font-weight:800; }
    .brand p { font-size:11px; opacity:.75; }
    .doc { text-align:left; }
    .doc .label { font-size:11px; opacity:.75; }
    .doc .title { font-size:15px; font-weight:800; color:#F5CE26; }
    .doc .num { font-size:13px; font-weight:700; letter-spacing:.5px; direction:ltr; }
    .info { display:grid; grid-template-columns:1.4fr 1fr; gap:20px; padding:22px 36px; border-bottom:1px solid #F0E4D3; }
    .section-label { font-size:10px; color:#B8935A; font-weight:700; letter-spacing:1px; margin-bottom:6px; }
    .info table td { padding:3px 0; vertical-align:top; }
    .info .k { color:#9ca3af; padding-left:14px; white-space:nowrap; }
    .info .v { font-weight:600; }
    .body { padding:22px 36px; }
    .category { margin-bottom:22px; break-inside:auto; }
    .cat-head { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:8px; background:#FAF5EE; border:1px solid #F0E4D3; border-bottom:none; border-radius:10px 10px 0 0; padding:10px 14px; break-after:avoid; }
    .cat-title { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:800; color:#3B210E; }
    .cat-index { width:22px; height:22px; border-radius:6px; background:#6B4A2E; color:#fff; font-size:11px; display:inline-flex; align-items:center; justify-content:center; }
    .cat-meta { display:flex; gap:12px; font-size:11px; color:#8F6A1B; font-weight:600; }
    table.items { width:100%; border-collapse:collapse; border:1px solid #F0E4D3; }
    .items th { background:#fff; font-size:10px; color:#9ca3af; font-weight:700; padding:8px 10px; text-align:right; border-bottom:2px solid #F0E4D3; }
    .items td { padding:8px 10px; border-bottom:1px solid #f3f4f6; }
    .items tr { break-inside:avoid; }
    .c { text-align:center !important; }
    .muted { color:#9ca3af; }
    .strong { font-weight:700; color:#3B210E; }
    .pos { color:#047857; font-weight:600; }
    .neg { color:#b91c1c; font-weight:600; }
    .pname { font-weight:600; }
    .barcode { font-size:10px; color:#9ca3af; direction:ltr; text-align:right; }
    .subtotal-label { text-align:left; font-weight:700; color:#6B4A2E; background:#FAF5EE; }
    .subtotal { font-weight:800; color:#3B210E; background:#FAF5EE; }
    .totals { display:flex; justify-content:flex-end; padding:0 36px 26px; break-inside:avoid; }
    .totals-box { min-width:320px; background:#FAF5EE; border:1px solid #F0E4D3; border-radius:12px; padding:16px 20px; }
    .totals-box .row { display:flex; justify-content:space-between; padding:3px 0; color:#4b5563; }
    .totals-box .row span:last-child { font-weight:700; color:#1f2937; }
    .grand { display:flex; justify-content:space-between; align-items:center; border-top:2px dashed #DBBF96; margin-top:10px; padding-top:10px; }
    .grand span:first-child { font-size:14px; font-weight:800; color:#3B210E; }
    .grand span:last-child { font-size:20px; font-weight:800; color:#6B4A2E; }
    .signatures { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; padding:10px 36px 28px; break-inside:avoid; }
    .sig { text-align:center; font-size:11px; color:#6b7280; }
    .sig .line { border-bottom:1px solid #d1d5db; height:40px; margin-bottom:6px; }
    .note { padding:0 36px 16px; font-size:10px; color:#9ca3af; }
    .footer { background:#FAF5EE; border-top:1px solid #F0E4D3; padding:14px 36px; display:flex; justify-content:space-between; font-size:10px; color:#9ca3af; }
    .empty { text-align:center; color:#9ca3af; padding:40px 0; }
    @media print {
      body { background:#fff; }
      .toolbar { display:none !important; }
      .sheet { box-shadow:none; border-radius:0; margin:0; max-width:none; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button class="btn-print" onclick="window.print()">طباعة / حفظ PDF</button>
    <button class="btn-close" onclick="window.close()">إغلاق</button>
  </div>

  <div class="sheet">
    <div class="header">
      <div class="brand">
        <div class="logo">H</div>
        <div>
          <h1>Havana House</h1>
          <p>نظام إدارة المبيعات</p>
        </div>
      </div>
      <div class="doc">
        <div class="title">${title}</div>
        <div class="label">رقم التقرير</div>
        <div class="num">${number}</div>
      </div>
    </div>

    <div class="info">
      <div>
        <div class="section-label">معايير التقرير</div>
        <table>${filterRows}</table>
      </div>
      <div>
        <div class="section-label">بيانات الإصدار</div>
        <table>
          <tr><td class="k">تاريخ الإصدار</td><td class="v">${formatDate(now)}</td></tr>
          <tr><td class="k">الوقت</td><td class="v">${now.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}</td></tr>
          <tr><td class="k">أصدر بواسطة</td><td class="v">${escapeHtml(issuedBy || '—')}</td></tr>
          <tr><td class="k">عدد التصنيفات</td><td class="v">${formatNumber(summary.categoriesCount)}</td></tr>
        </table>
      </div>
    </div>

    <div class="body">
      ${categorySections || '<p class="empty">لا توجد مبيعات مطابقة لمعايير التقرير</p>'}
    </div>

    <div class="totals">
      <div class="totals-box">
        ${totals}
        <div class="grand"><span>إجمالي المبيعات</span><span>${formatMoney(summary.revenue)}</span></div>
      </div>
    </div>

    ${financial ? '<p class="note">* التكلفة والربح تقديريان، محسوبان وفق سعر الشراء الحالي لكل منتج.</p>' : ''}

    <div class="signatures">
      <div class="sig"><div class="line"></div>المحاسب</div>
      <div class="sig"><div class="line"></div>المدير</div>
      <div class="sig"><div class="line"></div>الختم</div>
    </div>

    <div class="footer">
      <span>Havana House — تم إنشاء هذا المستند تلقائياً</span>
      <span>${number}</span>
    </div>
  </div>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return false;
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}
